import { useCallback, useEffect } from 'react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';

import boostSfx from '../assets/sounds/boost.mp3';
import falseAnswerSfx from '../assets/sounds/falseanswer.mp3';
import goodAnswerSfx from '../assets/sounds/goodanswer.mp3';
import jumpSfx from '../assets/sounds/jump.mp3';
import slowdownSfx from '../assets/sounds/slowdown.mp3';
import surfingSpeedwayGame from '../assets/sounds/surfing-speedway-game.mp3';
import switchLaneLeftSfx from '../assets/sounds/switchlane-left.mp3';
import switchLaneRightSfx from '../assets/sounds/switchlane-right.mp3';

import type { LaneChooserSoundEffect } from '../game/types';

type UseLaneChooserGameplayAudioOptions = {
	active: boolean;
};

const useLaneChooserGameplayAudio = ({ active }: UseLaneChooserGameplayAudioOptions) => {
	const { play: playGameMusic, stop: stopGameMusic } = useAudioPlayer({
		src: surfingSpeedwayGame,
		loop: true,
		volume: 0.5,
	});
	const { play: playGoodAnswer } = useAudioPlayer({
		src: goodAnswerSfx,
		volume: 0.7,
	});
	const { play: playFalseAnswer } = useAudioPlayer({
		src: falseAnswerSfx,
		volume: 0.7,
	});
	const { play: playSwitchLaneLeft, stop: stopSwitchLaneLeft } = useAudioPlayer({
		src: switchLaneLeftSfx,
		volume: 0.7,
	});
	const { play: playSwitchLaneRight, stop: stopSwitchLaneRight } = useAudioPlayer({
		src: switchLaneRightSfx,
		volume: 0.7,
	});
	const { play: playBoost, stop: stopBoost } = useAudioPlayer({
		src: boostSfx,
		volume: 0.7,
	});
	const { play: playSlowdown, stop: stopSlowdown } = useAudioPlayer({
		src: slowdownSfx,
		volume: 0.7,
	});
	const { play: playJump, stop: stopJump } = useAudioPlayer({
		src: jumpSfx,
		volume: 0.7,
	});

	const playAnswerFeedback = useCallback(
		(isCorrect: boolean) => {
			if (isCorrect) {
				playGoodAnswer();
				return;
			}

			playFalseAnswer();
		},
		[playFalseAnswer, playGoodAnswer],
	);

	const playSoundEffect = useCallback(
		(effect: LaneChooserSoundEffect) => {
			switch (effect) {
				case 'switch-lane-left':
					stopSwitchLaneLeft();
					playSwitchLaneLeft();
					break;
				case 'switch-lane-right':
					stopSwitchLaneRight();
					playSwitchLaneRight();
					break;
				case 'boost-start':
					stopBoost();
					playBoost();
					break;
				case 'boost-stop':
					stopBoost();
					break;
				case 'slowdown-start':
					stopSlowdown();
					playSlowdown();
					break;
				case 'slowdown-stop':
					stopSlowdown();
					break;
				case 'jump':
					stopJump();
					playJump();
					break;
			}
		},
		[
			playBoost,
			playJump,
			playSlowdown,
			playSwitchLaneLeft,
			playSwitchLaneRight,
			stopBoost,
			stopJump,
			stopSlowdown,
			stopSwitchLaneLeft,
			stopSwitchLaneRight,
		],
	);

	useEffect(() => {
		if (!active) {
			stopGameMusic();
			return;
		}

		playGameMusic();
		return () => {
			stopGameMusic();
		};
	}, [active, playGameMusic, stopGameMusic]);

	return {
		playAnswerFeedback,
		playSoundEffect,
	};
};

export default useLaneChooserGameplayAudio;
